import styles from "./SideNavigationCTA.module.scss";
import { ComponentType, useState } from "react";
import {
  IconCheckmark,
  IconClose,
  IconDelete,
  IconEdit,
  IconProps,
} from "../../icons";
import { useSideNavigationCollapseContext } from "../side-navigation-contexts";
import * as Popover from "@radix-ui/react-popover";
import { SideNavigationToolTip } from "../side-navigation-collapsed-menu-item-tooltip/SideNavigationToolTip";

export interface SideNavigationCTAProps {
  buttons: SideNavigationCTAButton[];
}

export interface SideNavigationCTAPopoutItem {
  label: string;
  value?: string | number | unknown;
}

export type SideNavigationCTAButton = {
  label: string;
  value?: string | number | unknown;
  onClick: (value?: SideNavigationCTAButton["value"]) => void;
  Icon?: ComponentType<IconProps>;
} & (
  | {
      isPopout: true;
      popoutTitle?: string;
      popoutItems: SideNavigationCTAPopoutItem[];
      onPopoutItemClick: (value?: SideNavigationCTAPopoutItem) => void;
      arePopoutItemsEditable: true;
      onDelete: (value?: SideNavigationCTAPopoutItem) => void;
    }
  | {
      isPopout: true;
      popoutTitle?: string;
      onPopoutItemClick: (value?: SideNavigationCTAPopoutItem) => void;
      popoutItems: SideNavigationCTAPopoutItem[];
      arePopoutItemsEditable?: false;
    }
  | {
      isPopout?: false;
      onDelete?: never;
    }
);

export const SideNavigationCTASection = (props: SideNavigationCTAProps) => {
  const { isCollapsed } = useSideNavigationCollapseContext();
  return (
    <section
      data-collapsed={isCollapsed}
      data-cta-even={props.buttons.length % 2 === 0}
      className={styles.CTASectionContainer}
    >
      {props.buttons.map((item, index) =>
        item.isPopout ? (
          <SideNavigationCTAPopout
            popoutTitle={item.popoutTitle ?? ""}
            isCollapsed={isCollapsed}
            {...item}
            key={index}
          />
        ) : (
          <SideNavigationCTAButton
            isCollapsed={isCollapsed}
            {...item}
            key={index}
          />
        ),
      )}
    </section>
  );
};

const SideNavigationCTAButton = (
  props: SideNavigationCTAButton & { isCollapsed: boolean },
) => {
  return (
    <SideNavigationToolTip text={props.label} condition={props.isCollapsed}>
      <button
        data-collapsed={props.isCollapsed}
        tabIndex={0}
        aria-label={props.label}
        onClick={() => props.onClick(props.value)}
        className={styles.buttonContainer}
      >
        {props.Icon && (
          <span className={styles.icon}>
            <props.Icon />
          </span>
        )}
        {!props.isCollapsed && (
          <span className={styles.label}>{props.label}</span>
        )}
      </button>
    </SideNavigationToolTip>
  );
};

const SideNavigationCTAPopout = (
  props: SideNavigationCTAButton & {
    isCollapsed: boolean;
    popoutTitle: string;
  },
) => {
  const [inEditMode, setInEditMode] = useState<boolean>(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] =
    useState<SideNavigationCTAPopoutItem | null>(null);

  const handleDeleteClick = (item: SideNavigationCTAPopoutItem) => {
    setItemToDelete(item);
    setIsConfirmingDelete(true);
  };

  const handleDeleteConfirmationClick = () => {
    if (itemToDelete && props.isPopout && props.arePopoutItemsEditable) {
      props.onDelete(itemToDelete);
    }
    setItemToDelete(null);
    setIsConfirmingDelete(false);
  };

  const handleCancelDelete = () => {
    setIsConfirmingDelete(false);
    setItemToDelete(null);
  };

  const popoverTrigger = (
    <>
      {props.Icon && (
        <span className={styles.icon}>
          <props.Icon />
        </span>
      )}
      {!props.isCollapsed && (
        <span className={styles.label}>{props.label}</span>
      )}
    </>
  );

  return (
    <Popover.Root modal onOpenChange={() => setInEditMode(false)}>
      <SideNavigationToolTip text={props.label} condition={props.isCollapsed}>
        <Popover.Trigger
          data-collapsed={props.isCollapsed}
          tabIndex={0}
          className={styles.buttonContainer}
        >
          {popoverTrigger}
        </Popover.Trigger>
      </SideNavigationToolTip>
      <Popover.Portal>
        <Popover.Content
          side={"right"}
          align={"start"}
          sideOffset={19}
          className={styles.popoverContainer}
        >
          {props.isPopout && props.popoutTitle && (
            <span className={styles.popoverTitle}>
              <span className={styles.titleText}>{props.popoutTitle}</span>
              {props.arePopoutItemsEditable && (
                <button
                  aria-label={inEditMode ? "Exit edit mode" : "Edit items"}
                  onClick={() => setInEditMode(!inEditMode)}
                  className={styles.editIconContainer}
                >
                  <span className={styles.innerContainer}>
                    {inEditMode ? (
                      <span className={styles.editIcon}>
                        <IconClose
                          data-editmode={inEditMode}
                          className={styles.editIcon}
                        />
                      </span>
                    ) : (
                      <IconEdit className={styles.editIcon} />
                    )}
                    <span className={styles.editText}>
                      {inEditMode ? "Exit" : "Edit"}
                    </span>
                  </span>
                </button>
              )}
            </span>
          )}
          {props.isPopout &&
            props.popoutItems.map((item, index) =>
              inEditMode ? (
                <div
                  data-editmode={inEditMode}
                  aria-label={item.label}
                  key={index}
                  className={styles.popoverItem}
                >
                  {item.label}
                  {isConfirmingDelete && itemToDelete === item ? (
                    <div className={styles.trashContainer}>
                      <button
                        tabIndex={0}
                        onClick={handleDeleteConfirmationClick}
                        aria-label={`Confirm deletion of ${item.label}`}
                        className={styles.deleteButton}
                      >
                        <IconCheckmark
                          className={`${styles.deleteIcon} ${styles.confirmIcon}`}
                        />
                      </button>
                      <button
                        tabIndex={0}
                        onClick={handleCancelDelete}
                        aria-label={`Cancel deletion of ${item.label}`}
                        className={styles.deleteButton}
                      >
                        <IconClose
                          className={`${styles.deleteIcon} ${styles.cancelIcon}`}
                        />
                      </button>
                    </div>
                  ) : (
                    inEditMode &&
                    props.isPopout &&
                    props.arePopoutItemsEditable && (
                      <button
                        tabIndex={0}
                        onClick={() => handleDeleteClick(item)}
                        aria-label={`Remove ${item.label}`}
                        className={styles.deleteButton}
                      >
                        <IconDelete className={styles.deleteIcon} />
                      </button>
                    )
                  )}
                </div>
              ) : (
                <button
                  tabIndex={0}
                  data-editmode={inEditMode}
                  aria-label={item.label}
                  key={index}
                  className={styles.popoverItem}
                  onClick={() =>
                    props.isPopout && props.onPopoutItemClick(item)
                  }
                >
                  {item.label}
                </button>
              ),
            )}
          <Popover.Arrow className={styles.popoverArrow} />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
